import type { Scene } from './types';
import { spawnCasing, spawnMuzzlePuff } from './effects';
import { SHOT_SFX, cull, type Mix } from './battleConfig';
import { playSfx } from '../game/sfx';
import type { PlaybackState } from './playbackState';
import type { Actors } from './actors';

let lastCasingLandAt = -1;

export const runDueEvents = (sc: Scene, state: PlaybackState, n: number, mix: Mix, actors: Actors, youId: string | null): void => {
  while (state.timeline.length && state.timeline[0].at <= n) state.timeline.shift()?.run(n);

  for (let i = state.pendingBlasts.length - 1; i >= 0; i--) {
    if (n < state.pendingBlasts[i].at) continue;
    const b = state.pendingBlasts.splice(i, 1)[0];
    const groundY = Math.max(b.cy, ...b.ev.hits.map((h) => sc.byId[h.id]?.y ?? b.cy));
    sc.blasts.push({ x: b.cx, y: b.cy, t0: n, life: 0.6, r: 150, groundY });
    playSfx('grenade_explode', 0.5);
    actors.commit(b.ev);
    for (const hit of b.ev.hits) {
      const f = sc.byId[hit.id];
      if (!f) continue;
      const toHp = actors.drainHit(f, hit.damage, hit.absorbed);
      if (toHp > 0) actors.popDamage(f, toHp, false);
    }
  }

  for (let i = state.pendingShots.length - 1; i >= 0; i--) {
    if (n < state.pendingShots[i].at) continue;
    const ps = state.pendingShots.splice(i, 1)[0];
    sc.bullets.push({ fromX: ps.fromX, fromY: ps.fromY, toX: ps.toX, toY: ps.toY, t0: n, life: 0.12 });
    sc.flashes.push({ x: ps.fromX, y: ps.fromY, angle: ps.angle, t0: n, life: 0.07 });
    const shooter = sc.byId[ps.shooter];
    spawnMuzzlePuff(sc, ps.fromX, ps.fromY, ps.angle, shooter ? shooter.y : ps.fromY, n);
    if (shooter) spawnCasing(sc, ps.fromX, ps.fromY, ps.angle, shooter.dir, shooter.y, ps.shooter === youId, n);
    playSfx(SHOT_SFX[ps.key] ?? SHOT_SFX.ak, mix(0.3, ps.shooter, ps.target));
  }

  for (const c of sc.casings) {
    if (c.landed || n < c.t0 + c.landT) continue;
    c.landed = true;
    if (Math.abs(n - lastCasingLandAt) >= 0.05) {
      lastCasingLandAt = n;
      const vol = c.mine ? 0.17 + Math.random() * 0.05 : 0.06 + Math.random() * 0.03;
      playSfx('casing_drop', vol);
    }
  }

  for (let i = state.pendingSmokes.length - 1; i >= 0; i--) {
    if (n < state.pendingSmokes[i].at) continue;
    const sm = state.pendingSmokes.splice(i, 1)[0];
    const scr = state.smokeScreens.get(sm.target);
    if (scr && scr.bornAt == null) scr.bornAt = n;
    playSfx('smoke_bloom', 0.45);
  }
};

export const cullEffects = (sc: Scene, n: number): void => {
  cull(sc.bullets, n); cull(sc.casings, n); cull(sc.muzzlePuffs, n); cull(sc.flashes, n); cull(sc.impacts, n); cull(sc.blasts, n); cull(sc.damages, n);
  cull(sc.slashes, n); cull(sc.flashbangs, n);
  for (let i = sc.grenades.length - 1; i >= 0; i--) if (n >= sc.grenades[i].land) { sc.grenades.splice(i, 1); playSfx('grenade_land', 0.4); }
};
